import { useEffect, useRef } from 'react';

/** Nearest scrollable ancestor (lead details tab panel uses overflow: auto). */
export function getOverflowScrollParent(el) {
  let p = el?.parentElement;
  while (p) {
    const st = window.getComputedStyle(p);
    const y = st.overflowY;
    const x = st.overflowX;
    const scrollable =
      /(auto|scroll|overlay)/.test(y) || /(auto|scroll|overlay)/.test(x);
    if (scrollable && p.scrollHeight > p.clientHeight) return p;
    p = p.parentElement;
  }
  return null;
}

/**
 * When the sentinel enters the tab scroll area, calls onLoadMore (scroll pagination).
 * @param {Object} opts
 * @param {boolean} opts.enabled — e.g. list has rows and not in full-page loading
 * @param {boolean} opts.hasNext
 * @param {boolean} opts.isBusy — initial load or append in flight (skip trigger)
 * @param {function} opts.onLoadMore
 * @param {unknown} [opts.watchKey] — bump when list/page changes so observer rebinds
 */
export function useLeadTabInfiniteScroll({
  enabled,
  hasNext,
  isBusy,
  onLoadMore,
  watchKey,
}) {
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const busyRef = useRef(isBusy);
  onLoadMoreRef.current = onLoadMore;
  busyRef.current = isBusy;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!enabled || !hasNext || !sentinel) return undefined;

    const root = getOverflowScrollParent(sentinel);
    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || busyRef.current) return;
        onLoadMoreRef.current();
      },
      { root: root || null, rootMargin: '180px', threshold: 0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [enabled, hasNext, watchKey]);

  return sentinelRef;
}
